import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Check, X, FileText, Clock, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { apiErr } from "@/context/AuthContext";
import { num, fmtDate } from "@/lib/format";
import { PageTitle, Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TABS = [
  { key: "MENUNGGU", label: "Menunggu" },
  { key: "PERLU_DITINJAU_ULANG", label: "Perlu Ditinjau" },
  { key: "DISETUJUI,DITOLAK", label: "Riwayat" },
];
const STATUS_STYLE = {
  MENUNGGU: "bg-amber-100 text-amber-700",
  DISETUJUI: "bg-emerald-100 text-emerald-700",
  DITOLAK: "bg-rose-100 text-rose-700",
  PERLU_DITINJAU_ULANG: "bg-orange-100 text-orange-700",
};
const MOD = { PENGAMBILAN: "Pengambilan", SETORAN: "Setoran" };

function OldList({ req }) {
  const trx = req.original?.transactions || [];
  const isSetor = req.source_module === "SETORAN";
  return (
    <div className="rounded-xl border border-slate-200 divide-y text-sm" data-testid="approval-old-list">
      {trx.map((t, i) => (
        <div key={i} className="px-3 py-2">
          {isSetor ? (
            <div>{t.product_name || t.product_id} · Setor {num(t.quantity)} · Rijek {num(t.repair_quantity || 0)} · Bagus {num(t.good_quantity)}</div>
          ) : t.transaction_type === "AMBIL" ? (
            <div>{t.ref_no ? `[${t.ref_no}] ` : ""}{t.product_name || t.product_id} · {num(t.quantity)} PCS</div>
          ) : (
            <div>Alat: {t.bon_item_name || t.bon_item_id} · {num(t.quantity)} {t.unit}</div>
          )}
        </div>
      ))}
      {!trx.length && <div className="px-3 py-2 text-slate-400">—</div>}
    </div>
  );
}

function ProposalList({ req, pname }) {
  const p = req.proposal || {};
  const isSetor = req.source_module === "SETORAN";
  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/40 divide-y divide-blue-100 text-sm" data-testid="approval-new-list">
      {isSetor ? (
        (p.setor_items || []).map((it, i) => (
          <div key={i} className="px-3 py-2">
            {pname(it.product_id)} · Setor {num(it.quantity)} · Rijek {num(it.reject || 0)}
            {it.keterangan ? ` · ${it.keterangan}` : ""}
          </div>
        ))
      ) : (
        <>
          {(p.items || []).map((it, i) => (
            <div key={`a${i}`} className="px-3 py-2">{it.ref_no ? `[${it.ref_no}] ` : ""}{pname(it.product_id)} · {num(it.quantity)} PCS</div>
          ))}
          {(p.tools || []).map((it, i) => (
            <div key={`t${i}`} className="px-3 py-2">Alat: {pname(it.bon_item_id)} · {num(it.quantity)} {it.unit}</div>
          ))}
        </>
      )}
      {(p.worker_name) && <div className="px-3 py-2 text-xs text-slate-500">Pengepul: {p.worker_name} · Tanggal: {fmtDate(p.transaction_date)}</div>}
    </div>
  );
}

export default function AdminApprovals() {
  const [tab, setTab] = useState("MENUNGGU");
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [bon, setBon] = useState([]);
  const [confirm, setConfirm] = useState(null); // {req, kind}
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/op/corrections?status=${tab}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/op/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/op/bon-items").then((r) => setBon(r.data)).catch(() => {});
  }, []);

  const pname = (id) =>
    products.find((p) => p.product_id === id)?.product_name ||
    bon.find((b) => b.id === id)?.item_name || id;

  const act = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const url = `/op/corrections/${confirm.req.request_id}/${confirm.kind}`;
      await api.post(url, { note: note.trim() || null });
      toast.success(confirm.kind === "approve" ? "Permohonan disetujui" : "Permohonan ditolak");
      setConfirm(null); setNote(""); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="w-full pb-10" data-testid="admin-approvals">
      <PageTitle title="Persetujuan" desc="Permohonan koreksi & pembatalan transaksi dari operator pencatat" />

      <div className="flex gap-2 mb-4">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${tab === t.key ? "bg-[#0071E3] text-white" : "bg-white text-slate-600 border border-slate-200"}`}
            data-testid={`approval-tab-${t.key.split(",")[0].toLowerCase()}`}>{t.label}</button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400 text-sm" data-testid="approval-empty">
          <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />Tidak ada permohonan.
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((req) => {
            const pending = req.status === "MENUNGGU";
            const isCancel = req.action === "PEMBATALAN";
            return (
              <Panel key={req.request_id} testId={`approval-card-${req.request_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-blue-700">{req.request_id}</span>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${isCancel ? "bg-rose-100 text-rose-700" : "bg-blue-100 text-blue-700"}`}>{req.action}</span>
                      <span className="text-sm text-slate-500">{MOD[req.source_module] || req.source_module} · <span className="font-mono">{req.op_no}</span></span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Diajukan oleh {req.requested_by_name} · {fmtDate(req.requested_at)} · {req.worker_name || "-"}
                    </div>
                  </div>
                  <span className={`shrink-0 text-[11px] font-bold px-2 py-1 rounded-full ${STATUS_STYLE[req.status] || "bg-slate-100 text-slate-600"}`} data-testid={`approval-status-${req.request_id}`}>{req.status.replace(/_/g, " ")}</span>
                </div>

                <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-sm text-slate-700 mb-3">
                  <span className="text-xs font-bold text-slate-500">ALASAN · </span>{req.reason}
                </div>

                {isCancel ? (
                  <div>
                    <div className="text-xs font-bold text-slate-500 mb-2">TRANSAKSI YANG AKAN DIBATALKAN</div>
                    <OldList req={req} />
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4 items-start">
                    <div>
                      <div className="text-xs font-bold text-slate-500 mb-2">DATA LAMA</div>
                      <OldList req={req} />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-[#0071E3] mb-2 flex items-center gap-1"><ArrowRight className="w-3.5 h-3.5" />USULAN BARU</div>
                      <ProposalList req={req} pname={pname} />
                    </div>
                  </div>
                )}

                {req.resolve_note && (
                  <div className="mt-3 text-xs text-slate-500"><FileText className="w-3 h-3 inline mr-1" />Catatan admin: {req.resolve_note}</div>
                )}
                {req.result_op_no && (
                  <div className="mt-1 text-xs text-emerald-700">Hasil: struk <span className="font-mono">{req.result_op_no}</span></div>
                )}

                {pending && (
                  <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
                    <Button variant="outline" className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => { setConfirm({ req, kind: "reject" }); setNote(""); }} data-testid={`approval-reject-${req.request_id}`}>
                      <X className="w-4 h-4 mr-1" />Tolak
                    </Button>
                    <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => { setConfirm({ req, kind: "approve" }); setNote(""); }} data-testid={`approval-approve-${req.request_id}`}>
                      <Check className="w-4 h-4 mr-1" />Setujui
                    </Button>
                  </div>
                )}
                {req.status === "PERLU_DITINJAU_ULANG" && (
                  <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
                    <Button variant="outline" className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => { setConfirm({ req, kind: "reject" }); setNote(""); }} data-testid={`approval-reject-${req.request_id}`}>
                      <X className="w-4 h-4 mr-1" />Tolak (perlu diajukan ulang)
                    </Button>
                  </div>
                )}
              </Panel>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="approval-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === "approve" ? "Setujui" : "Tolak"} permohonan {confirm?.req.request_id}?
            </AlertDialogTitle>
            <AlertDialogDescription className="no-up">
              {confirm?.kind === "approve"
                ? (confirm?.req.action === "PEMBATALAN"
                    ? "Seluruh transaksi pada struk ini akan dibatalkan. Saldo WIP akan divalidasi otomatis."
                    : "Struk revisi baru akan dibuat dan struk lama ditandai SUDAH DIKOREKSI. Saldo WIP akan divalidasi otomatis.")
                : "Permohonan akan ditolak dan operator dapat mengajukan ulang bila perlu."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (opsional)" className="mt-1" data-testid="approval-note" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="approval-confirm-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={act} disabled={busy}
              className={confirm?.kind === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
              data-testid="approval-confirm-ok">{busy ? "Memproses…" : (confirm?.kind === "approve" ? "Ya, Setujui" : "Ya, Tolak")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
